import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Flex,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { TriangleUpIcon } from "@chakra-ui/icons";
import { useState, useEffect, useCallback } from "react";
import { checkActiveDisruptions, getUncategorizedSummary } from "../../api/uncategorized";

import UncategorizedTransactionModal from "./uncategorizedTransactionModal";

const UncategorizedNotificationBanner = ({ customerId, onRefresh }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [summary, setSummary] = useState(null);
  const [activeDisruptions, setActiveDisruptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check for active disruptions
      const disruptionsData = await checkActiveDisruptions(customerId);
      const activeDisruptionsList = disruptionsData.results || disruptionsData;
      setActiveDisruptions(activeDisruptionsList);
      
      // Get summary for transaction counts and amounts
      const summaryData = await getUncategorizedSummary(customerId);
      setSummary(summaryData);
    } catch (error) {
      console.error("Error fetching uncategorized data:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId && !dismissed) {
      fetchData();
    }
  }, [customerId, dismissed, fetchData]);

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in session storage to remember for this session
    sessionStorage.setItem(`uncategorized_dismissed_${customerId}`, "true");
  };

  const handleViewDetails = () => {
    onOpen();
  };

  const handleModalClose = () => {
    onClose();
    // Refresh the data after modal closes
    fetchData();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Check if dismissed in session storage
  useEffect(() => {
    const isDismissed = sessionStorage.getItem(`uncategorized_dismissed_${customerId}`);
    if (isDismissed === "true") {
      setDismissed(true);
    }
  }, [customerId]);

  // Check if there are any active soft disruptions
  const hasActiveSoftDisruption = activeDisruptions.some(
    disruption => disruption.disruption_type === 'soft' && disruption.status === 'active'
  );

  // Don't show if loading, dismissed, or no active soft disruptions
  if (loading || dismissed || !hasActiveSoftDisruption) {
    return null;
  }

  return (
    <>
      <Alert
        status="warning"
        backgroundColor={"#FEF5E7"}
        borderColor={"#F6AD55"}
        borderWidth={"1px"}
        borderRadius={"8px"}
        marginBottom={"16px"}
        zIndex={100}
      >
        <AlertIcon as={TriangleUpIcon} color={"#DD6B20"} />
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <HStack spacing={3}>
            <AlertTitle color={"#DD6B20"} fontWeight="bold">
              Uncategorized Transactions
            </AlertTitle>
            <AlertDescription color={"#DD6B20"}>
              You have {summary?.active_uncategorized_count || 0} uncategorized transaction{(summary?.active_uncategorized_count || 0) !== 1 ? 's' : ''} 
              totaling {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(summary?.total_uncategorized_amount || 0)}
            </AlertDescription>
          </HStack>
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              onClick={handleViewDetails}
            >
              View Details
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color={"#DD6B20"}
              onClick={handleDismiss}
            >
              Dismiss
            </Button>
          </HStack>
        </Flex>
      </Alert>

      <UncategorizedTransactionModal
        isOpen={isOpen}
        onClose={handleModalClose}
        customerId={customerId}
        summary={summary}
      />
    </>
  );
};

export default UncategorizedNotificationBanner; 